import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Divider, CircularProgress, Alert, Card, CardContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { apiFetch } from '../api';

const Rojmel = () => {
  const [accounts, setAccounts] = useState([]);
  const [daybookData, setDaybookData] = useState({ opening_cash: 0, vouchers: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Entry Form State
  const [entry, setEntry] = useState({
    type: 'Payment', // Payment = Kharch, Receipt = Jam
    account_id: '',
    amount: '',
    narration: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
        const accRes = await apiFetch('/api/accounts');
        setAccounts(await accRes.json());
        
        const dayRes = await apiFetch(`/api/daybook?date=${date}`);
        setDaybookData(await dayRes.json());
    } catch (err) {
        setError('डेटा लोड करताना त्रुटी आली.');
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  const handleSaveEntry = async () => {
    if (!entry.account_id || !entry.amount) {
      alert('कृपया खाते आणि रक्कम भरा.');
      return;
    }

    setLoading(true);
    setError('');
    
    // Convert Jam/Kharch to Double Entry
    // If Kharch (Payment): Debit Account, Credit Cash
    // If Jam (Receipt): Debit Cash, Credit Account
    const cashAccount = accounts.find(a => a.name.includes('कॅश अकाऊंट'));
    if (!cashAccount) {
        setError('कॅश अकाऊंट सिस्टिम मध्ये सापडले नाही.');
        setLoading(false);
        return;
    }

    const entries = [];
    if (entry.type === 'Payment') {
        entries.push({ account_id: entry.account_id, debit: entry.amount, credit: 0 });
        entries.push({ account_id: cashAccount.id, debit: 0, credit: entry.amount });
    } else {
        entries.push({ account_id: cashAccount.id, debit: entry.amount, credit: 0 });
        entries.push({ account_id: entry.account_id, debit: 0, credit: entry.amount });
    }

    try {
      const response = await apiFetch('/api/journal-entries', {
        method: 'POST',
        body: JSON.stringify({
           date,
           voucher_type: entry.type,
           narration: entry.narration,
           entries
        })
      });

      if (response.ok) {
        setSuccess('व्यवहार नोंदवला गेला!');
        setEntry({ type: 'Payment', account_id: '', amount: '', narration: '' });
        fetchData();
      } else {
        setError('नोंदवताना अडचण आली.');
      }
    } catch (err) {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setLoading(false);
    }
  };

  const calculateClosing = () => {
    let totalJam = 0;
    let totalKharch = 0;
    const cashAccountId = accounts.find(a => a.name.includes('कॅश अकाऊंट'))?.id;

    daybookData.vouchers.forEach(v => {
        v.entries.forEach(e => {
            if (e.account_id === cashAccountId) {
                totalJam += parseFloat(e.debit_amount);
                totalKharch += parseFloat(e.credit_amount);
            }
        });
    });

    return daybookData.opening_cash + totalJam - totalKharch;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
            जमा-खर्च / रोजमेळ (Daybook)
        </Typography>
        <TextField 
            type="date" size="small" 
            value={date} onChange={(e) => setDate(e.target.value)} 
            sx={{ bgcolor: 'white' }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Left: Daybook Summary */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#f0fdf4', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">आजचा तपशील</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>दिनांक: {new Date(date).toLocaleDateString('mr-IN')}</Typography>
            </Box>
            
            <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead sx={{ bgcolor: '#eee' }}>
                    <TableRow>
                        <TableCell>वेळ / प्रकार</TableCell>
                        <TableCell>तपशील</TableCell>
                        <TableCell align="right">नावे (खर्च)</TableCell>
                        <TableCell align="right">जमा (इनकम)</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow sx={{ fontStyle: 'italic', bgcolor: '#fafafa' }}>
                        <TableCell colSpan={3} align="right">आरंभीची शिल्लक (Opening Cash):</TableCell>
                        <TableCell align="right">₹ {daybookData.opening_cash}</TableCell>
                    </TableRow>
                    {daybookData.vouchers.map((v, i) => (
                        <TableRow key={i}>
                            <TableCell>{new Date(v.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} / {v.voucher_type}</TableCell>
                            <TableCell>{v.narration}</TableCell>
                            <TableCell align="right">
                                {v.voucher_type === 'Payment' || v.voucher_type === 'Purchase' ? 
                                    v.entries.reduce((s, e) => s + (e.account?.group?.type === 'Expense' || e.account?.group?.type === 'Liability' ? parseFloat(e.debit_amount) : 0), 0) : '-'}
                            </TableCell>
                            <TableCell align="right">
                                {v.voucher_type === 'Receipt' ? 
                                    v.entries.reduce((s, e) => s + (e.account?.group?.type === 'Income' || e.account?.group?.type === 'Asset' ? parseFloat(e.credit_amount) : 0), 0) : '-'}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                        <TableCell colSpan={3} align="right"><strong>Closing Cash:</strong></TableCell>
                        <TableCell align="right"><strong>₹ {calculateClosing()}</strong></TableCell>
                    </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Right: Entry Form */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1B5E20' }}>नवीन नोंद</Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>प्रकार</InputLabel>
                <Select value={entry.type} label="प्रकार" onChange={(e) => setEntry({...entry, type: e.target.value})}>
                    <MenuItem value="Payment">खर्च (Payment)</MenuItem>
                    <MenuItem value="Receipt">जमा (Income)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>खाते निवडा</InputLabel>
                <Select value={entry.account_id} label="खाते निवडा" onChange={(e) => setEntry({...entry, account_id: e.target.value})}>
                    {accounts.filter(a => !a.name.includes('कॅश अकाऊंट')).map(a => (
                        <MenuItem key={a.id} value={a.id}>{a.name} ({a.group?.name})</MenuItem>
                    ))}
                </Select>
              </FormControl>

              <TextField fullWidth label="रक्कम (Amount)" type="number" value={entry.amount} onChange={(e) => setEntry({...entry, amount: e.target.value})} sx={{ mb: 2 }} />
              
              <TextField fullWidth label="तपशील / रिमार्क" multiline rows={2} value={entry.narration} onChange={(e) => setEntry({...entry, narration: e.target.value})} sx={{ mb: 3 }} />

              <Button 
                fullWidth variant="contained" 
                startIcon={<SaveIcon />} onClick={handleSaveEntry} disabled={loading}
                sx={{ backgroundColor: '#0FA958', py: 1.5, fontWeight: 'bold' }}
              >
                नोंद जतन करा
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Rojmel;
